/**
 * QUEM É QUEM NO CAMPO, dentro do cartão dele: as pessoas e as congregações.
 *
 * Pedido do ensaio de 15/10/2026, em três partes:
 *
 * 1. **o cartão mostra o e-mail de cada pessoa e se o envio do link falhou**, pela resposta do
 *    serviço de e-mail. Antes ele dizia "3 pessoas ainda não criaram a senha", e a pergunta que
 *    chega por telefone é QUEM, e se o e-mail chegou;
 * 2. **quem ainda não criou a senha se corrige aqui**: nome, e-mail, cargo, congregação e limite.
 *    Trocar o e-mail derruba o link antigo e manda um novo só para ela. Quem já criou a senha não
 *    se corrige aqui: o cadastro dela agora é da sede, e a tela diz isso;
 * 3. **a congregação se acrescenta com o Pastor Dirigente junto**, mostrando antes as que já
 *    existem, e se renomeia enquanto não tem notebook.
 *
 * Quem decide o que pode é o SERVIDOR: a tela esconde o botão que não serve, e o servidor recusa
 * do mesmo jeito se alguém chamar a rota por fora.
 */
import { useMemo, useState, type FormEvent, type JSX } from 'react'
import { ehSessaoTerminada, ErroDoServidor } from '../../api'
import {
  administracao, CARGOS, nomeDoCargo, type CampoNaLista, type CargoDaTela, type EnvioDoLink,
  type PessoaNoCartao
} from '../../api-da-administracao'
import { emReais } from '../../dinheiro'
import { emCentavos } from './CampoNovo'

/** Qual formulário do cadastro está aberto. Um por vez, como o resto da tela. */
export type FocoDoCadastro =
  | { o: 'corrigir'; pessoaId: string }
  | { o: 'tirar'; pessoaId: string }
  | { o: 'desligar-fator'; pessoaId: string }
  | { o: 'congregacao-nova' }
  | { o: 'renomear'; congregacaoId: string }

/** `2026-10-15T18:38:52Z` vira `15/10 às 15:38`, na hora de quem está lendo. */
function quando(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const dia = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
  return `${dia} às ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** A frase do envio, e se ela é de alerta. É a linha que responde "o e-mail chegou?". */
function comoFoiOEnvio(envio: EnvioDoLink): { frase: string; alerta: boolean } {
  switch (envio.como) {
    case 'NUNCA':
      return { frase: 'nenhum link mandado ainda', alerta: false }
    case 'NA_FILA':
      return { frase: `link na fila de envio desde ${quando(envio.quando)}`, alerta: false }
    case 'ENVIADO':
      return {
        frase: `link entregue ao serviço de e-mail em ${quando(envio.quando)}. Ainda não criou a senha`,
        alerta: false
      }
    case 'TENTANDO_DE_NOVO':
      return { frase: `o envio falhou e vai ser tentado de novo: ${envio.porque}`, alerta: true }
    case 'FALHOU':
      return {
        frase: `o envio do link FALHOU em ${quando(envio.quando)}: ${envio.porque}. Confira o e-mail e mande de novo`,
        alerta: true
      }
  }
}

function oLimite(p: PessoaNoCartao): string {
  if (p.limite_aprovacao === null) return 'aprova sem limite'
  if (p.limite_aprovacao === 0) return 'não aprova'
  return `aprova até ${emReais(p.limite_aprovacao)}`
}

function sortearOPedido(): string {
  const sorteio =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `tela-${sorteio}`
}

export function OCadastro({
  campo,
  foco,
  aoFocar,
  aoMudar,
  aoSair
}: {
  campo: CampoNaLista
  /** O formulário aberto NESTE cartão, ou nulo. */
  foco: FocoDoCadastro | null
  aoFocar: (foco: FocoDoCadastro | null) => void
  /** Algo mudou no servidor: a lista recarrega. */
  aoMudar: () => void
  aoSair: () => void
}): JSX.Element {
  /*
   * O RECADO E O ERRO FICAM AQUI, no cartão, e não no alto da página: no celular, um aviso
   * escrito lá em cima com a pessoa olhando o cartão lá embaixo é um aviso que ninguém lê.
   */
  const [recado, setRecado] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState<string | null>(null)

  function falhou(falha: unknown, padrao: string): void {
    if (ehSessaoTerminada(falha)) return aoSair()
    setErro(falha instanceof ErroDoServidor ? falha.message : padrao)
  }

  async function mandarOLink(p: PessoaNoCartao): Promise<void> {
    setErro(null)
    setRecado(null)
    setOcupado(p.id)
    try {
      await administracao.mandarOLinkDe(campo.id, p.id)
      setRecado(`Um link novo para ${p.nome} foi posto na fila, para ${p.email}. Ele vale 48 horas.`)
      aoMudar()
    } catch (falha) {
      falhou(falha, 'Não foi possível mandar o link.')
    } finally {
      setOcupado(null)
    }
  }

  const congregacoes = campo.lista_de_congregacoes

  return (
    <section className="cadastro-do-campo">
      <p className="titulo-do-cadastro">Pessoas</p>

      {recado !== null && <p className="recado feito" role="status">{recado}</p>}
      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <ul className="pessoas-do-campo">
        {campo.gente.map((p) => {
          const envio = comoFoiOEnvio(p.envio)
          return (
            <li key={p.id}>
              <span className="quem">
                <strong>{p.nome}</strong>
                {' · '}{nomeDoCargo(p.cargo)}
                {' · '}{p.congregacao}
              </span>
              <span className="email">{p.email === '' ? 'sem e-mail' : p.email}</span>
              <span className="detalhe">
                {oLimite(p)}
                {p.tem_conta_de_painel ? ' · tem conta de painel' : ''}
              </span>

              {p.criou_a_senha ? (
                <span className="envio pronto">
                  Já criou a senha. Correções, agora, no programa da sede.
                </span>
              ) : (
                <>
                  <span className={envio.alerta ? 'envio alerta' : 'envio'}>{envio.frase}</span>
                  <span className="acoes-da-pessoa">
                    <button className="mini" onClick={() => aoFocar({ o: 'corrigir', pessoaId: p.id })}>
                      Corrigir
                    </button>
                    <button
                      className="mini"
                      disabled={ocupado === p.id}
                      onClick={() => void mandarOLink(p)}
                    >
                      {ocupado === p.id ? 'Mandando...' : 'Mandar o link de novo'}
                    </button>
                    {p.cargo !== 'pastor_do_campo' && (
                      <button className="mini perigo" onClick={() => aoFocar({ o: 'tirar', pessoaId: p.id })}>
                        Tirar
                      </button>
                    )}
                  </span>
                </>
              )}

              {foco?.o === 'corrigir' && foco.pessoaId === p.id && (
                <FormularioDaPessoa
                  campo={campo}
                  pessoa={p}
                  aoDesistir={() => aoFocar(null)}
                  aoFeito={(frase) => { setErro(null); setRecado(frase); aoFocar(null); aoMudar() }}
                  aoSair={aoSair}
                />
              )}

              {/*
                O fator de quem perdeu o celular E os códigos (18/10/2026). Só para quem tem conta
                no painel com o fator ligado; a senha não muda por aqui.
              */}
              {p.conta_de_painel?.tem_segundo_fator === true && foco?.o !== 'desligar-fator' && (
                <span className="acoes-da-pessoa">
                  <button className="mini perigo" onClick={() => aoFocar({ o: 'desligar-fator', pessoaId: p.id })}>
                    Desligar o segundo fator
                  </button>
                </span>
              )}

              {foco?.o === 'desligar-fator' && foco.pessoaId === p.id && p.conta_de_painel !== null && (
                <DesligarOFator
                  pessoa={p}
                  contaId={p.conta_de_painel.id}
                  aoDesistir={() => aoFocar(null)}
                  aoFeito={(frase) => { setErro(null); setRecado(frase); aoFocar(null); aoMudar() }}
                  aoSair={aoSair}
                />
              )}

              {foco?.o === 'tirar' && foco.pessoaId === p.id && (
                <ConfirmarTirar
                  campo={campo}
                  pessoa={p}
                  aoDesistir={() => aoFocar(null)}
                  aoFeito={(frase) => { setErro(null); setRecado(frase); aoFocar(null); aoMudar() }}
                  aoSair={aoSair}
                />
              )}
            </li>
          )
        })}
      </ul>

      <p className="titulo-do-cadastro">Congregações</p>

      <ul className="congregacoes-do-campo">
        {congregacoes.map((c) => (
          <li key={c.id}>
            <span>
              {c.nome}
              {c.e_sede ? ' (sede)' : ''}
            </span>
            {c.pode_renomear && foco?.o !== 'renomear' && (
              <button className="mini" onClick={() => aoFocar({ o: 'renomear', congregacaoId: c.id })}>
                Renomear
              </button>
            )}
            {foco?.o === 'renomear' && foco.congregacaoId === c.id && (
              <FormularioDoNome
                campoId={campo.id}
                congregacaoId={c.id}
                nomeDeAgora={c.nome}
                aoDesistir={() => aoFocar(null)}
                aoFeito={(frase) => { setErro(null); setRecado(frase); aoFocar(null); aoMudar() }}
                aoSair={aoSair}
              />
            )}
          </li>
        ))}
      </ul>

      {foco?.o === 'congregacao-nova' ? (
        <FormularioDaCongregacao
          campo={campo}
          aoDesistir={() => aoFocar(null)}
          aoFeito={(frase) => { setErro(null); setRecado(frase); aoFocar(null); aoMudar() }}
          aoSair={aoSair}
        />
      ) : (
        <p className="linha-form">
          <button className="mini" onClick={() => aoFocar({ o: 'congregacao-nova' })}>
            Acrescentar congregação
          </button>
        </p>
      )}
    </section>
  )
}

/* =============================================================== corrigir uma pessoa */

function FormularioDaPessoa({
  campo,
  pessoa,
  aoDesistir,
  aoFeito,
  aoSair
}: {
  campo: CampoNaLista
  pessoa: PessoaNoCartao
  aoDesistir: () => void
  aoFeito: (frase: string) => void
  aoSair: () => void
}): JSX.Element {
  const [nome, setNome] = useState(pessoa.nome)
  const [email, setEmail] = useState(pessoa.email)
  const [cargo, setCargo] = useState<CargoDaTela>(
    (CARGOS.find((c) => c.valor === pessoa.cargo)?.valor ?? 'pastor_dirigente')
  )
  const [congregacaoId, setCongregacaoId] = useState(pessoa.congregacao_id)
  const [limite, setLimite] = useState(
    pessoa.limite_aprovacao === null || pessoa.limite_aprovacao === 0
      ? ''
      : (pessoa.limite_aprovacao / 100).toFixed(2).replace('.', ',')
  )
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  const oCargo = CARGOS.find((c) => c.valor === cargo)!
  /* O Pastor do Campo é um só: ele não troca de cargo aqui, e ninguém vira Pastor do Campo. */
  const cargosQueServem = pessoa.cargo === 'pastor_do_campo'
    ? CARGOS.filter((c) => c.valor === 'pastor_do_campo')
    : CARGOS.filter((c) => c.valor !== 'pastor_do_campo')

  async function salvar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)

    const centavos = oCargo.semLimite ? null : oCargo.limiteZero ? 0 : emCentavos(limite)
    if (!oCargo.semLimite && !oCargo.limiteZero && centavos === null) {
      return setErro(
        `Falta o limite de aprovação de ${nome.trim()}. Deixar em branco quer dizer SEM LIMITE, ` +
        'e só o Pastor do Campo é assim.'
      )
    }

    setIndo(true)
    try {
      const feito = await administracao.corrigirAPessoa(campo.id, pessoa.id, {
        nome: nome.trim(),
        email: email.trim(),
        cargo,
        congregacao_id: congregacaoId,
        limite_aprovacao: centavos
      })
      aoFeito(
        feito.mudou.length === 0
          ? `Nada mudou no cadastro de ${nome.trim()}.`
          : `O cadastro de ${nome.trim()} foi corrigido (${feito.mudou.join(', ')}).` +
            (feito.link_mandado
              ? ` Um link novo foi posto na fila para ${email.trim()}, e o do e-mail antigo deixou de valer.`
              : '') +
            (feito.avisos.length > 0 ? ` ${feito.avisos.join(' ')}` : '')
      )
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível corrigir.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir corrigir-pessoa" onSubmit={(e) => void salvar(e)}>
      <p className="explicacao">
        <strong>{pessoa.nome} ainda não criou a senha</strong>, e por isso o cadastro dela se
        corrige aqui. Trocando o e-mail, o link antigo deixa de valer e um novo vai só para ela.
      </p>

      <label>
        Nome
        <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={200} required />
      </label>

      <label>
        E-mail
        <input
          className="campo"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoCapitalize="none"
          spellCheck={false}
          maxLength={200}
          required
        />
      </label>

      <label>
        Cargo
        <select
          className="campo"
          value={cargo}
          onChange={(e) => setCargo(e.target.value as CargoDaTela)}
        >
          {cargosQueServem.map((c) => <option key={c.valor} value={c.valor}>{c.titulo}</option>)}
        </select>
        <span className="ajuda-do-codigo">{oCargo.explicacao}</span>
      </label>

      <label>
        Congregação
        <select className="campo" value={congregacaoId} onChange={(e) => setCongregacaoId(e.target.value)}>
          {campo.lista_de_congregacoes.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}{c.e_sede ? ' (sede)' : ''}</option>
          ))}
        </select>
      </label>

      {!oCargo.semLimite && !oCargo.limiteZero && (
        <label>
          Limite de aprovação, em reais
          <input
            className="campo"
            value={limite}
            onChange={(e) => setLimite(e.target.value)}
            inputMode="decimal"
            placeholder="500,00"
            maxLength={14}
          />
          <span className="ajuda-do-codigo">
            <strong>Em branco quer dizer sem limite</strong>, e por isso este número é obrigatório.
          </span>
        </label>
      )}

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button type="submit" disabled={indo}>{indo ? 'Salvando...' : 'Salvar a correção'}</button>
        <button type="button" onClick={aoDesistir} disabled={indo}>Deixar como está</button>
      </div>
    </form>
  )
}

/* ===================================================================== tirar a pessoa */

function ConfirmarTirar({
  campo,
  pessoa,
  aoDesistir,
  aoFeito,
  aoSair
}: {
  campo: CampoNaLista
  pessoa: PessoaNoCartao
  aoDesistir: () => void
  aoFeito: (frase: string) => void
  aoSair: () => void
}): JSX.Element {
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function tirar(): Promise<void> {
    setErro(null)
    setIndo(true)
    try {
      await administracao.tirarAPessoa(campo.id, pessoa.id)
      aoFeito(`${pessoa.nome} saiu do cadastro de ${campo.nome}. O link dela deixou de valer.`)
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível tirar.')
      setIndo(false)
    }
  }

  return (
    <div className="confirmar-reabrir">
      <p className="recado atencao" role="note">
        Tirar {pessoa.nome} do cadastro? Ela ainda não criou a senha, então nada do que foi lançado
        no Campo é dela. A saída vai para os notebooks pela fila, e fica na trilha.
      </p>
      {erro !== null && <p className="recado" role="alert">{erro}</p>}
      <div className="linha-form">
        <button className="perigo" disabled={indo} onClick={() => void tirar()}>
          {indo ? 'Tirando...' : `Tirar ${pessoa.nome}`}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>Deixar como está</button>
      </div>
    </div>
  )
}

/* ============================================================ desligar o segundo fator */

/**
 * Desligar o segundo fator de uma pessoa, para quem perdeu o aplicativo E os códigos.
 *
 * Pede o motivo, que fica na trilha, e o código do aplicativo DESTA conta, de quem hospeda. A pessoa
 * sai de todo lugar onde estiver dentro, recebe um aviso por e-mail e, na próxima entrada, cadastra
 * o aplicativo de novo antes de ver qualquer Campo. A senha dela não muda.
 */
function DesligarOFator({
  pessoa,
  contaId,
  aoDesistir,
  aoFeito,
  aoSair
}: {
  pessoa: PessoaNoCartao
  contaId: string
  aoDesistir: () => void
  aoFeito: (frase: string) => void
  aoSair: () => void
}): JSX.Element {
  const [motivo, setMotivo] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function desligar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)
    try {
      const feito = await administracao.desligarOSegundoFatorDe(contaId, motivo.trim(), codigo.trim())
      aoFeito(
        `O segundo fator de ${pessoa.nome} foi desligado. Na próxima entrada, ela cadastra o aplicativo ` +
        `de novo antes de ver qualquer coisa.${feito.aviso_por_email ? ' Um aviso foi posto na fila para o e-mail dela.' : ''}`
      )
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível desligar.')
      setCodigo('')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void desligar(e)}>
      <p className="recado atencao" role="note">
        Desligar o segundo fator de {pessoa.nome}? É para quem perdeu o celular e os códigos de
        emergência. Ela sai de todo lugar onde estiver dentro, recebe um aviso por e-mail e, na próxima
        entrada, com a mesma senha, cadastra o aplicativo de novo. O motivo fica na trilha.
      </p>
      {erro !== null && <p className="recado" role="alert">{erro}</p>}
      <label>
        Motivo
        <input
          className="campo"
          type="text"
          name="motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: perdeu o celular e o papel dos códigos; pediu por telefone em 18/10"
          required
          minLength={5}
          maxLength={500}
        />
      </label>
      <label>
        Código do aplicativo desta conta
        <input
          className="campo"
          type="text"
          name="codigo"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          spellCheck={false}
          maxLength={20}
          required
        />
      </label>
      <div className="linha-form">
        <button className="perigo" type="submit" disabled={indo}>
          {indo ? 'Desligando...' : 'Desligar o segundo fator'}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>Deixar como está</button>
      </div>
    </form>
  )
}

/* ============================================================ acrescentar congregação */

function FormularioDaCongregacao({
  campo,
  aoDesistir,
  aoFeito,
  aoSair
}: {
  campo: CampoNaLista
  aoDesistir: () => void
  aoFeito: (frase: string) => void
  aoSair: () => void
}): JSX.Element {
  /* Sorteado quando o formulário abre: o duplo clique devolve a mesma congregação. */
  const pedidoId = useMemo(sortearOPedido, [])
  const [nome, setNome] = useState('')
  const [comDirigente, setComDirigente] = useState(true)
  const [nomeDele, setNomeDele] = useState('')
  const [email, setEmail] = useState('')
  const [limite, setLimite] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function acrescentar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)

    if (nome.trim() === '') return setErro('Falta o nome da congregação.')
    const centavos = emCentavos(limite)
    if (comDirigente) {
      if (nomeDele.trim() === '') return setErro('Falta o nome do Pastor Dirigente.')
      if (email.trim() === '') return setErro('Falta o e-mail do Pastor Dirigente: é por ele que chega o link.')
      if (centavos === null) {
        return setErro(
          'Falta o limite de aprovação do Pastor Dirigente. Em branco quer dizer SEM LIMITE, e só o ' +
          'Pastor do Campo é assim.'
        )
      }
    }

    setIndo(true)
    try {
      const feito = await administracao.acrescentarACongregacao(campo.id, {
        pedido_id: pedidoId,
        nome: nome.trim(),
        ...(comDirigente && centavos !== null
          ? { dirigente: { nome: nomeDele.trim(), email: email.trim(), limite_aprovacao: centavos } }
          : {})
      })
      aoFeito(
        `${nome.trim()} foi acrescentada a ${campo.nome}.` +
        (feito.link_mandado ? ` O link de ${nomeDele.trim()} foi posto na fila, para ${email.trim()}.` : '') +
        (feito.avisos.length > 0 ? ` ${feito.avisos.join(' ')}` : '')
      )
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível acrescentar.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir congregacao-nova" onSubmit={(e) => void acrescentar(e)}>
      {/*
        A LISTA DO QUE JÁ EXISTE VEM ANTES DO FORMULÁRIO, pedido do ensaio: quem acrescenta tem de
        ver se a congregação já está lá com outro nome antes de criar uma segunda.
      */}
      <p className="explicacao">
        <strong>Já estão neste Campo:</strong>{' '}
        {campo.lista_de_congregacoes.map((c) => c.nome).join(', ')}.
      </p>

      <label>
        Nome da congregação nova
        <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} required />
      </label>

      <label className="escolha">
        <input type="checkbox" checked={comDirigente} onChange={(e) => setComDirigente(e.target.checked)} />
        {' '}Cadastrar o Pastor Dirigente dela agora
      </label>

      {comDirigente && (
        <>
          <label>
            Nome do Pastor Dirigente
            <input className="campo" value={nomeDele} onChange={(e) => setNomeDele(e.target.value)} maxLength={200} />
          </label>
          <label>
            E-mail do Pastor Dirigente
            <input
              className="campo"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoCapitalize="none"
              spellCheck={false}
              maxLength={200}
            />
            <span className="ajuda-do-codigo">
              Ele recebe o link de criar a senha, e nasce com conta de painel para aprovar pelo celular.
            </span>
          </label>
          <label>
            Limite de aprovação, em reais
            <input
              className="campo"
              value={limite}
              onChange={(e) => setLimite(e.target.value)}
              inputMode="decimal"
              placeholder="500,00"
              maxLength={14}
            />
            <span className="ajuda-do-codigo">
              <strong>Em branco quer dizer sem limite</strong>, e por isso este número é obrigatório.
            </span>
          </label>
        </>
      )}

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button type="submit" disabled={indo}>{indo ? 'Acrescentando...' : 'Acrescentar a congregação'}</button>
        <button type="button" onClick={aoDesistir} disabled={indo}>Desistir</button>
      </div>
    </form>
  )
}

/* ================================================================ renomear congregação */

function FormularioDoNome({
  campoId,
  congregacaoId,
  nomeDeAgora,
  aoDesistir,
  aoFeito,
  aoSair
}: {
  campoId: string
  congregacaoId: string
  nomeDeAgora: string
  aoDesistir: () => void
  aoFeito: (frase: string) => void
  aoSair: () => void
}): JSX.Element {
  const [nome, setNome] = useState(nomeDeAgora)
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function renomear(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)
    try {
      const feito = await administracao.renomearACongregacao(campoId, congregacaoId, nome.trim())
      aoFeito(feito.mudou ? `${nomeDeAgora} agora se chama ${nome.trim()}.` : 'O nome continua o mesmo.')
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível renomear.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void renomear(e)}>
      <label>
        Novo nome de {nomeDeAgora}
        <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} required />
        <span className="ajuda-do-codigo">
          Só enquanto nenhum notebook é desta congregação. O nome novo chega a todos os notebooks do
          Campo pela fila.
        </span>
      </label>
      {erro !== null && <p className="recado" role="alert">{erro}</p>}
      <div className="linha-form">
        <button type="submit" disabled={indo || nome.trim() === ''}>{indo ? 'Renomeando...' : 'Renomear'}</button>
        <button type="button" onClick={aoDesistir} disabled={indo}>Deixar como está</button>
      </div>
    </form>
  )
}
